import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconChevronRightOutline18 as ChevronRight } from "nucleo-ui-outline-18"
import { z } from "zod"

import type { Workspace } from "@productlane/zero"
import { MembershipRole } from "@productlane/db/browser"
import {
	AlertDialog,
	Button,
	Form,
	FormInput,
	IntegrationCard,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useSession } from "@/lib/session"
import { useAutosaveText } from "@/lib/use-autosave-text"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { WorkspaceLogoField } from "@/screens/settings/workspace-logo-field"
import { DeleteWorkspaceModal } from "../delete-workspace-modal"
import {
	RECONNECT_TOOLTIP,
	useIntegrationConnections,
	useIntegrationReconnects,
	useVisibleIntegrations,
} from "../integrations/registry"
import { SettingsHeader } from "../settings-header"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"

const GeneralFormSchema = z.object({
	name: z.string().min(1),
})

export function GeneralSettingsPage() {
	const workspace = useZeroWorkspace()

	if (!workspace) {
		return null
	}

	return (
		<>
			<SettingsHeader title="General" />
			<GeneralForm workspace={workspace} />
		</>
	)
}

interface GeneralFormProps {
	workspace: Workspace
}

function GeneralForm({ workspace }: GeneralFormProps) {
	const form = useZodForm({
		schema: GeneralFormSchema,
		defaultValues: {
			name: workspace.name,
		},
	})

	const { role } = useRole()
	const navigate = useNavigate()
	const [deleteWorkspaceModalOpen, setDeleteWorkspaceModalOpen] =
		useState(false)
	const [leaveDialogOpen, setLeaveDialogOpen] = useState(false)

	const leaveWorkspaceMutation = api.workspace.leaveWorkspace.useMutation({
		onSuccess: () => {
			void navigate({ to: "/logout" })
		},
		onError: (error) => {
			toast.error({
				title: "Cannot leave workspace",
				description: error.message,
			})
		},
	})
	const ctx = api.useUtils()
	const { session } = useSession()
	const zero = useZero()

	const refreshWorkspace = async () => {
		await ctx.workspace.info.invalidate()
		await ctx.user.getMembership.invalidate()
	}

	const persist = async (values: { name: string }) => {
		if (!session?.workspaceId) return
		try {
			await mutateOk(
				zero.mutate(
					mutators.workspaces.update({
						id: session.workspaceId,
						name: values.name,
					}),
				),
			)
			await refreshWorkspace()
			analytics.track("update-general-settings")
		} catch (error) {
			toast.error({
				title: "Could not save",
				description: error instanceof Error ? error.message : undefined,
			})
		}
	}

	const nameWatch = form.watch("name")
	// An empty name fails the schema, so it never autosaves.
	useAutosaveText({
		value: nameWatch,
		persistedValue: workspace.name,
		onSave: (value) => persist({ name: value }),
		enabled: !!form.formState.dirtyFields.name && nameWatch.trim().length > 0,
	})

	return (
		<>
			<Form
				className="flex flex-col gap-2"
				form={form}
				onSubmit={() => undefined}
			>
				<div className="text-sm font-medium">Workspace settings</div>
				<SettingsContainer className="mt-1">
					<SettingItem
						title="Logo"
						description={`256×256 px recommended. SVG may not display in emails.`}
						action={
							<WorkspaceLogoField
								workspaceId={workspace.id}
								logoUrl={workspace.logoUrl ?? null}
								onUploaded={refreshWorkspace}
							/>
						}
					/>

					<SettingItem
						title="Workspace name"
						className="pt-4"
						description="Display name for your workspace."
						action={
							<FormInput
								name="name"
								readOnly={role === "VIEWER"}
								className="max-w-60"
								onBlur={(e) => {
									const value = e.target.value.trim()
									if (form.formState.dirtyFields.name && value.length > 0) {
										void persist({ name: value })
									}
								}}
							/>
						}
					/>
				</SettingsContainer>

				{role !== "VIEWER" && <IntegrationsSummary />}

				<div className="mt-8 text-sm font-medium">Danger zone</div>
				<SettingsContainer className="mt-1">
					<SettingItem
						title="Leave workspace"
						description="Remove yourself from this workspace. You can be re-invited later."
						action={
							<Button
								variant="danger-ghost"
								onClick={(e) => {
									e.preventDefault()
									setLeaveDialogOpen(true)
								}}
							>
								Leave workspace
							</Button>
						}
					/>

					{session?.membershipRole === MembershipRole.ADMIN && (
						<SettingItem
							className="pt-4"
							title="Delete workspace"
							description="Delete all data and member accounts used only here."
							action={
								<Button
									onClick={() => setDeleteWorkspaceModalOpen(true)}
									variant="danger-ghost"
								>
									Delete workspace
								</Button>
							}
						/>
					)}
				</SettingsContainer>
			</Form>
			<DeleteWorkspaceModal
				open={deleteWorkspaceModalOpen}
				onClose={() => setDeleteWorkspaceModalOpen(false)}
			/>

			<AlertDialog
				isOpen={leaveDialogOpen}
				onOpenChange={setLeaveDialogOpen}
				title={`Leave ${workspace.name}?`}
				description="You will lose access to this workspace. This action cannot be undone."
				confirmText="Leave"
				onCancel={() => setLeaveDialogOpen(false)}
				onConfirm={async () => {
					await leaveWorkspaceMutation.mutateAsync()
				}}
			/>
		</>
	)
}

function IntegrationsSummary() {
	const navigate = useNavigate()
	const integrations = useVisibleIntegrations()
	const connections = useIntegrationConnections()
	const reconnects = useIntegrationReconnects()
	const enabled = integrations.filter(
		(integration) => connections[integration.key],
	)
	const shown = enabled.length > 0 ? enabled : integrations

	return (
		<>
			<div className="mt-8 flex items-center justify-between">
				<div className="text-sm font-medium">Integrations</div>
				<Button
					variant="ghost"
					size="sm"
					iconRight={ChevronRight}
					onClick={() => void navigate({ to: "/settings/integrations" })}
				>
					View all
				</Button>
			</div>
			<div className="mt-1 grid grid-cols-1 gap-3 sm:-mx-3.5 sm:grid-cols-3">
				{shown.map((integration) => (
					<IntegrationCard
						key={integration.key}
						variant="compact"
						name={integration.title}
						connected={connections[integration.key]}
						warning={
							reconnects[integration.key] ? RECONNECT_TOOLTIP : undefined
						}
						logo={<integration.Icon className="h-5 w-5" />}
						onClick={() => void navigate({ href: integration.href })}
					/>
				))}
			</div>
		</>
	)
}
